import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, Copy, Loader2, RefreshCw, Send, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  draftReplies,
  mapAddresseeGenderContext,
  mapSpeakerGender,
  ReplyDraftsQuotaError,
  type ReplyDraftOption,
  type ReplyDraftsUsage,
} from '@/lib/replyDrafts/api'
import { GatewayAuthError } from '@/lib/gateway'
import { usePaywall } from '../paywall'
import { WsIconButton } from './workspace'

export interface ReplyDraftsSheetProps {
  /** Controls visibility. The component stays mounted regardless, so its
   *  drafts/usage cache survives a close/reopen of the SAME message —
   *  mirrors the mobile sheet's "a pair that already exists just opens, no
   *  request, no spend" rule. */
  open: boolean
  onClose: () => void
  /** The incoming Hebrew message (the target pane's source text on the
   *  he-to-other direction). */
  sourceText: string
  /** Its translation — what the learner actually read. */
  translatedText: string
  /** Target-language code, e.g. 'en'. */
  language: string
  languageNative?: string
  /** Raw web `GenderId` addressee token ('to_male', 'general', …); mapped to
   *  the server's 'speaking-to-*' vocabulary inside this component. */
  genderContext?: string
  /** Raw web `GenderId` speaker token ('male' | 'female' | 'general', …). */
  userGender?: string
}

type SheetError = { message: string; kind: 'quota' | 'unavailable' | 'generic' }

/**
 * "Reply in Hebrew" panel: two ready-to-send Hebrew replies for a message the
 * learner just translated, each with a plain-language gloss underneath so
 * they know exactly what they are about to send.
 *
 * Desktop renders a 420px panel anchored to the right edge of the window
 * (the target pane's edge, since it is the rightmost column); mobile renders
 * a bottom sheet. Both share the same state and copy.
 */
export function ReplyDraftsSheet({
  open,
  onClose,
  sourceText,
  translatedText,
  language,
  languageNative,
  genderContext,
  userGender,
}: ReplyDraftsSheetProps) {
  const { open: openPaywall } = usePaywall()
  const [options, setOptions] = useState<ReplyDraftOption[]>([])
  const [usage, setUsage] = useState<ReplyDraftsUsage | undefined>(undefined)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<SheetError | null>(null)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  // "Or say what you mean" — free text the learner can give when the two
  // guessed replies miss; regenerates around it, and counts against the same
  // quota as any other generation. Mirrors mobile's ReplyDraftsSheet exactly
  // (same copy, same "clears on close, not on a successful submit" rule).
  const [intent, setIntent] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // A different message replaces whatever pair was showing — the same rule
  // the mobile sheet applies when the result screen shows a new translation.
  useEffect(() => {
    setOptions([])
    setUsage(undefined)
    setError(null)
    setCopiedIndex(null)
    setIntent('')
  }, [sourceText, translatedText])

  // A fresh open starts with an empty field — a half-typed intent from last
  // time reads as something the learner already asked for. Mirrors mobile's
  // identical `useEffect` in ReplyDraftsSheet.tsx.
  useEffect(() => {
    if (!open) setIntent('')
  }, [open])

  useEffect(() => () => {
    abortRef.current?.abort()
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
  }, [])

  const generate = useCallback(async (intentOverride?: string) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setIsGenerating(true)
    setError(null)
    try {
      const result = await draftReplies({
        sourceText,
        translatedText,
        language,
        languageNative,
        intent: intentOverride,
        genderContext: mapAddresseeGenderContext(genderContext),
        userGender: mapSpeakerGender(userGender),
        abortSignal: controller.signal,
      })
      if (controller.signal.aborted) return
      setOptions(result.options)
      setUsage(result.usage)
    } catch (err) {
      if (controller.signal.aborted) return
      if (err instanceof ReplyDraftsQuotaError) {
        // The client does not pre-check a quota counter (there is no
        // persisted usage store on web the way mobile's subscriptionStore
        // is) — the server's 429 IS the check, and it routes straight to
        // the paywall rather than an error message.
        onClose()
        openPaywall('reply-drafts')
        return
      }
      if (err instanceof GatewayAuthError) {
        setError({ message: 'Reply Drafts Are Not Available Yet', kind: 'unavailable' })
        return
      }
      setError({ message: err instanceof Error ? err.message : 'Could not draft replies right now.', kind: 'generic' })
    } finally {
      if (!controller.signal.aborted) setIsGenerating(false)
    }
  }, [sourceText, translatedText, language, languageNative, genderContext, userGender, onClose, openPaywall])

  // Auto-generate on open when there is nothing cached to show yet.
  useEffect(() => {
    if (open && options.length === 0 && !isGenerating && !error) void generate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleCopy = async (option: ReplyDraftOption, index: number) => {
    try {
      await navigator.clipboard.writeText(option.hebrew)
    } catch {
      return
    }
    setCopiedIndex(index)
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
    copyTimerRef.current = setTimeout(() => setCopiedIndex((current) => (current === index ? null : current)), 1500)
  }

  const handleSubmitIntent = () => {
    const trimmed = intent.trim()
    if (!trimmed || isGenerating) return
    void generate(trimmed)
  }

  if (!open) return null

  const remaining = usage?.limit != null ? Math.max(0, usage.limit - usage.used) : null

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/45 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Reply in Hebrew"
        className={cn(
          'fixed z-[101] inset-x-0 bottom-0 dvh-max-85 rounded-t-2xl animate-fade-up',
          'md:inset-x-auto md:inset-y-0 md:right-0 md:left-auto md:bottom-auto md:top-0',
          'md:w-[420px] md:max-h-none md:rounded-none md:border-l flex flex-col shadow-token-lg',
        )}
        style={{ background: 'var(--card)', borderColor: 'var(--line)', borderWidth: 1, borderBottom: 0 }}
      >
        <div className="flex items-center justify-between px-4 py-3 shrink-0 border-b" style={{ borderColor: 'var(--line-hair)' }}>
          <span className="text-[15px] font-semibold" style={{ color: 'var(--ink)' }}>Reply in Hebrew</span>
          <WsIconButton icon={<X size={16} />} label="Close" onClick={onClose} />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-3">
          {isGenerating && options.length === 0 ? (
            <div className="flex items-center gap-2 py-8 justify-center" role="status">
              <Loader2 size={16} className="animate-spin" style={{ color: 'var(--accent)' }} />
              <span className="text-[13px]" style={{ color: 'var(--muted)' }}>Writing Two Replies…</span>
            </div>
          ) : null}

          {error && options.length === 0 ? (
            <div className="flex flex-col items-start gap-2.5 py-4">
              <div className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--err)' }}>
                <AlertTriangle size={15} className="shrink-0 mt-0.5" aria-hidden />
                <span>{error.message}</span>
              </div>
              <button
                type="button"
                onClick={() => void generate()}
                className="inline-flex items-center gap-1.5 px-2.5 h-8 text-[12.5px] font-medium transition-colors hover:bg-surface-2"
                style={{ borderRadius: 6, border: '1px solid var(--line)', color: 'var(--ink)' }}
              >
                <RefreshCw size={12} aria-hidden /> Try Again
              </button>
            </div>
          ) : null}

          {options.map((option, index) => (
            <div
              key={`${option.hebrew}-${index}`}
              className="flex flex-col gap-2 px-3.5 py-3"
              style={{ borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card-2)' }}
            >
              <div
                dir="rtl"
                className="font-hebrew"
                style={{ fontSize: 19, lineHeight: 1.5, color: 'var(--ink)' }}
              >
                {option.hebrew}
              </div>
              <div className="text-[13px] leading-relaxed" style={{ color: 'var(--muted)' }}>{option.gloss}</div>
              <div className="flex items-center gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={() => void handleCopy(option, index)}
                  className="inline-flex items-center gap-1.5 px-2.5 h-7 text-[12px] font-medium transition-colors hover:bg-surface-2"
                  style={{ borderRadius: 6, border: '1px solid var(--line)', color: copiedIndex === index ? 'var(--ok)' : 'var(--muted)' }}
                >
                  {copiedIndex === index ? <Check size={12} /> : <Copy size={12} />}
                  {copiedIndex === index ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          ))}

          {options.length > 0 && !isGenerating ? (
            <button
              type="button"
              onClick={() => void generate()}
              className="self-start inline-flex items-center gap-1.5 px-2.5 h-8 text-[12.5px] font-medium transition-colors hover:bg-surface-2"
              style={{ borderRadius: 6, border: '1px solid var(--line)', color: 'var(--ink)' }}
            >
              <RefreshCw size={12} aria-hidden /> Regenerate
            </button>
          ) : null}

          {options.length > 0 && isGenerating ? (
            <div className="flex items-center gap-2 py-1" role="status">
              <Loader2 size={13} className="animate-spin" style={{ color: 'var(--accent)' }} />
              <span className="text-[12px]" style={{ color: 'var(--muted)' }}>Writing Two Replies…</span>
            </div>
          ) : null}

          {options.length > 0 ? (
            <div className="flex flex-col gap-1.5 mt-1">
              <span className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--muted-2)' }}>
                Or say what you mean
              </span>
              <div
                className="flex items-center gap-2 px-3 py-2"
                style={{ borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card-2)' }}
              >
                <input
                  type="text"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmitIntent() } }}
                  placeholder="e.g. yes, but 4pm suits me better"
                  disabled={isGenerating}
                  aria-label="Or say what you mean"
                  className="flex-1 min-w-0 bg-transparent text-[13.5px] outline-none"
                  style={{ color: 'var(--ink)' }}
                />
                <button
                  type="button"
                  onClick={handleSubmitIntent}
                  disabled={!intent.trim() || isGenerating}
                  aria-label="Write this in Hebrew"
                  className="shrink-0 inline-flex items-center justify-center disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
                  style={{ width: 24, height: 24, color: 'var(--accent-deep)' }}
                >
                  {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {remaining !== null && usage ? (
          <div className="px-4 py-2.5 shrink-0 border-t text-[11.5px] font-mono" style={{ borderColor: 'var(--line-hair)', color: 'var(--muted-2)' }}>
            {remaining} of {usage.limit} Free Drafts Left This Month
          </div>
        ) : null}
      </div>
    </>
  )
}
